import {
  FileDropField,
  FileList,
  Paragraph,
  Stack,
  Wizard,
  useWizardPage,
  Link,
  Title
} from '@efast_public/fjd-component-library';
import React, { useCallback, useEffect, useState } from 'react';
import { LogoVO } from '../../../../api/hooks/useLogo';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BehApiRoutes, behClient } from '../../../../api/beh-api';
import { validateLogo } from '../../../Settings/SettingsTabs/Logo/LogoSettings';
import { Trans, useTranslation } from 'react-i18next';
import { useNotification } from '../../../../shared/notification/useNotification';
import {
  base64ToBytes,
  fileToLogoV0
} from '../../../../shared/utils/fileUtils';
import { FJD_DOCS_EINRICHTUNGSASSISTANT } from '../../../../shared/utils/Links';

export default function Logo() {
  const { t } = useTranslation();
  const { notifyUserFromRequest, notifyUser } = useNotification();
  const queryClient = useQueryClient();
  const [droppedFile, setDroppedFile] = useState<File | undefined>(undefined);

  const { data: logoVO } = useQuery<LogoVO>({
    queryKey: [BehApiRoutes.logo()]
  });
  const { mutateAsync: deleteLogo, isPending: isDeleting } = useMutation({
    mutationFn: () =>
      notifyUserFromRequest(() => behClient.delete(BehApiRoutes.logo()), {
        successMessage: t('api.success.deleteLogo'),
        errorMessage: t('api.error.deleteLogo')
      }),
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: [BehApiRoutes.logo()]
      });
      setDroppedFile(undefined);
    }
  });

  const { mutateAsync: updateLogo, isPending: isUpdating } = useMutation({
    mutationFn: ({ payload }: { payload: LogoVO }) =>
      notifyUserFromRequest(() => behClient.put(BehApiRoutes.logo(), payload), {
        successMessage: t('api.success.updateLogo'),
        errorMessage: t('api.error.updateLogo')
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [BehApiRoutes.logo()]
      });
    }
  });
  useEffect(() => {
    if (logoVO) {
      setDroppedFile(
        new File([base64ToBytes(logoVO.base64Data)], logoVO.fileName, {
          type: logoVO.fileType
        })
      );
    }
  }, [logoVO]);

  const removeItem = () => {
    setDroppedFile(undefined);
  };

  const putOrDeleteLogo = useCallback(async () => {
    if (logoVO?.fileName === droppedFile?.name) {
      return;
    }
    if (droppedFile) {
      return fileToLogoV0(droppedFile).then((file) => {
        updateLogo({ payload: file }).catch(() => {});
      });
    } else if (droppedFile === undefined && logoVO) {
      return deleteLogo().catch(() => {});
    }
  }, [droppedFile, deleteLogo, logoVO, updateLogo]);

  const { wizardState } = useWizardPage({
    isFormValid: true,
    headline: (state) =>
      `Schritt ${state.currentPageIndex + 1} von ${state.pagesLength}`,
    onSubmit: putOrDeleteLogo,
    skipButtonLabel: 'Später einrichten',
    isStepOptional: true
  });

  return (
    <Wizard.Page>
      <Stack spacing="md">
        <Title level={2} visualLevel={6}>
          {t('configAssistent.logo.headline')}
        </Title>
        <Paragraph>
          {t('configAssistent.logo.descriptionText')}
          <Trans
            i18nKey="configAssistent.configAssistentDocumentationNotice"
            components={{
              Link: (
                <Link href={FJD_DOCS_EINRICHTUNGSASSISTANT} target="_blank">
                  hier
                </Link>
              )
            }}
          />
        </Paragraph>
        <Stack alignItems="stretch" spacing="xs">
          <FileDropField
            data-testid={'logoSettingsFileDrop'}
            label="Logo auswählen"
            disabled={isUpdating || isDeleting}
            dropHelperText="oder in dieses Feld ziehen"
            onAdd={(files) => {
              const errors = validateLogo(files);
              if (errors.length > 0) {
                notifyUser({ intent: 'error', title: errors.at(0) ?? '' });
                return;
              } else {
                setDroppedFile(files[0]);
              }
            }}
          />

          {droppedFile && (
            <FileList data-testid="logoSettingsFileList">
              <FileList.FileItem
                file={droppedFile}
                onDelete={() => removeItem()}
              />
            </FileList>
          )}
        </Stack>
      </Stack>
    </Wizard.Page>
  );
}
